/**
 * The coach's panel (ROUND7 §5): a heading with the station and the count, the instruction, and a dimmer
 * second line. It lives inside whichever HUD is on screen - the drop HUD in the air, the ground HUD's
 * top-centre column after touchdown - so it is scaled, placed and hidden exactly as they are, and the pause
 * menu (a later child of the ground HUD) covers it. The DOM is touched only when a line changes.
 */

import './coach.css';

export interface PanelView {
  /** "TRAINING - THE KNIFE". */
  head: string;
  /** "9/16", or empty. */
  count: string;
  text: string;
  note: string;
  /** The note is a warning (wounded, seen): drawn in the warning colour. */
  urgent: boolean;
  /** "DONE: SNEAK" for a moment after a step is ticked; empty otherwise. */
  flash: string;
  /** The course is being stopped. */
  stopping: boolean;
}

function div(className: string): HTMLElement {
  const node = document.createElement('div');
  node.className = className;
  return node;
}

export class CoachPanel {
  readonly el: HTMLElement;
  private readonly head: HTMLElement;
  private readonly count: HTMLElement;
  private readonly text: HTMLElement;
  private readonly note: HTMLElement;
  private readonly flash: HTMLElement;
  private last = '';
  /** Which HUD it was last put in; null before the first tick. */
  private airborne: boolean | null = null;

  /** `textSize`: the Options text size, for the drop HUD, which does not carry the ground HUD's. */
  constructor(textSize: number) {
    this.el = div('coach-panel coach-hidden');
    this.el.setAttribute('role', 'status');
    this.el.style.setProperty('--coach-ts', String(textSize));
    const bar = div('coach-head');
    this.head = div('coach-station');
    this.count = div('coach-count');
    bar.append(this.head, this.count);
    this.text = div('coach-text');
    this.note = div('coach-note');
    this.flash = div('coach-flash coach-hidden');
    this.el.append(bar, this.flash, this.text, this.note);
  }

  /**
   * Put the panel in the HUD that is on screen: the drop HUD while airborne, the ground HUD's top-centre column
   * after. Found fresh each time, so it follows a HUD that was built after the coach was mounted.
   */
  attach(root: HTMLElement, airborne: boolean): void {
    // Settled already: nothing to look up (this runs every tick).
    if (this.airborne === airborne && this.el.parentElement !== null && this.el.parentElement !== root) return;
    const host = airborne ? root.querySelector<HTMLElement>('.fly-root')
      : root.querySelector<HTMLElement>('.hud-root')?.querySelector<HTMLElement>('.hud-tc') ?? null;
    this.airborne = airborne;
    const target = host ?? root;
    if (this.el.parentElement === target) return;
    target.append(this.el);
    this.el.classList.toggle('coach-air', host !== null && airborne);
    this.el.classList.toggle('coach-loose', host === null);
  }

  show(view: PanelView | null): void {
    const key = view === null ? '' : JSON.stringify(view);
    if (key === this.last) return;
    this.last = key;
    this.el.classList.toggle('coach-hidden', view === null);
    if (view === null) return;
    this.head.textContent = view.head;
    this.count.textContent = view.count;
    this.text.textContent = view.text;
    this.note.textContent = view.note;
    this.note.classList.toggle('coach-hidden', view.note === '');
    this.note.classList.toggle('coach-urgent', view.urgent);
    this.flash.textContent = view.flash;
    this.flash.classList.toggle('coach-hidden', view.flash === '');
    this.el.classList.toggle('coach-stop', view.stopping);
  }

  dispose(): void {
    this.el.remove();
  }
}
